import React from "react";
import { Dialog, DialogContent, DialogTitle } from "@mui/material";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import { useFormik } from "formik";
import { Autocomplete } from "components/Autocomplete";
import { TextField } from "components/TextField";
import { MultilineTextField } from "components/MultilineTextField";
import * as Yup from "yup";

const validationSchema = Yup.object({
  levelType: Yup.object()
    .label("Threshold severity")
    .required()
    .nullable(),
  site: Yup.object()
    .label("Site")
    .nullable(),
  machine: Yup.object()
    .label("Machine")
    .nullable(),
  level: Yup.number()
    .label("Threshold level")
    .required(),
  recommendation: Yup.string()
    .label("Recommendation")
    .max(500)
});

export const RuleLevelDialog = ({
  open,
  title,
  initialValues,
  loading,
  error,
  errorMessage = undefined,
  handleSelectSiteId,
  sites,
  loadingSites,
  machines,
  loadingMachines,
  severityTypes,
  loadingSeverityTypes,
  confirmButtonLabel = "Confirm",
  onClose = () => {},
  onSubmit = () => {}
}) => {
  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: (values, { resetForm }) => {
      onSubmit(values, resetForm);
    }
  });

  const handleClose = () => {
    onClose();
    formik.resetForm();
  };

  const onChangeSite = (_event, site) => {
    formik.setFieldValue("site", site);
    formik.setFieldValue("machine", null);
    handleSelectSiteId(site?.id);
  };

  return (
    <Dialog maxWidth="sm" open={open} onClose={onClose}>
      <form onSubmit={formik.handleSubmit} noValidate>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                label="Threshold severity"
                name="levelType"
                id="levelType"
                required
                loading={loading || loadingSeverityTypes}
                options={severityTypes}
                getOptionLabel={levelType => levelType.name}
                value={formik.values.levelType}
                onChange={(e, value) =>
                  formik.setFieldValue("levelType", value)
                }
                onBlur={formik.handleBlur}
                error={
                  formik.touched.levelType && Boolean(formik.errors.levelType)
                }
                helperText={formik.touched.levelType && formik.errors.levelType}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                label="Site"
                name="site"
                id="site"
                loading={loadingSites || loading}
                options={sites}
                getOptionLabel={site => site.name}
                value={formik.values.site}
                onChange={onChangeSite}
                onBlur={formik.handleBlur}
                error={formik.touched.site && Boolean(formik.errors.site)}
                helperText={formik.touched.site && formik.errors.site}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                label="Machine"
                name="machine"
                id="machine"
                disabled={!formik.values.site}
                loading={loading || loadingMachines}
                options={machines ?? []}
                getOptionLabel={machine => machine.name}
                value={formik.values.machine}
                onChange={(e, value) => formik.setFieldValue("machine", value)}
                onBlur={formik.handleBlur}
                error={formik.touched.machine && Boolean(formik.errors.machine)}
                helperText={formik.touched.machine && formik.errors.machine}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Threshold level"
                name="level"
                id="level"
                type="number"
                fullWidth
                required
                disabled={loading}
                value={formik.values.level}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.level && Boolean(formik.errors.level)}
                helperText={formik.touched.level && formik.errors.level}
              />
            </Grid>
            <Grid item xs={12}>
              <MultilineTextField
                label="Recommendation"
                name="recommendation"
                id="recommendation"
                fullWidth
                disabled={loading}
                value={formik.values.recommendation}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.recommendation &&
                  Boolean(formik.errors.recommendation)
                }
                helperText={
                  formik.touched.recommendation && formik.errors.recommendation
                }
              />
            </Grid>
          </Grid>
          {error && (
            <ErrorSection>
              <Typography color="error">
                {errorMessage
                  ? errorMessage
                  : "An error occurred while creating the threshold, please try again."}
              </Typography>
            </ErrorSection>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="primary" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" type="submit">
            {confirmButtonLabel}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

RuleLevelDialog.propTypes = {
  open: PropTypes.bool,
  title: PropTypes.string,
  initialValues: PropTypes.object,
  loading: PropTypes.bool,
  error: PropTypes.bool,
  errorMessage: PropTypes.string,
  handleSelectSiteId: PropTypes.func,
  sites: PropTypes.array,
  loadingSites: PropTypes.bool,
  machines: PropTypes.array,
  loadingMachines: PropTypes.bool,
  severityTypes: PropTypes.array,
  loadingSeverityTypes: PropTypes.bool,
  confirmButtonLabel: PropTypes.string,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func
};

const ErrorSection = styled("div")`
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  justify-content: flex-end;
  padding-top: ${props => props.theme.spacing(2)};
`;
